import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Recycle,
  BookOpen,
  HelpCircle,
  Leaf,
  TreeDeciduous,
  Droplets,
  Package,
  Trash2,
  ArrowRight,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

const HomePage = () => {
  const features = [
    {
      icon: BookOpen,
      title: 'Rūšiavimo ABC',
      description: '150+ daiktų su detaliais rūšiavimo instrukcijomis nuo A iki Ž',
      link: '/rusiavimo-abc',
      color: 'bg-recycle-glass',
    },
    {
      icon: Recycle,
      title: 'Kaip Rūšiuoti',
      description: 'Praktiniai patarimai ir gairės teisingam atliekų rūšiavimui',
      link: '/kaip-rusiuoti',
      color: 'bg-recycle-paper',
    },
    {
      icon: HelpCircle,
      title: 'Mitai ir DUK',
      description: 'Dažniausiai užduodami klausimai ir paneigti mitai apie rūšiavimą',
      link: '/mitai-ir-duk',
      color: 'bg-recycle-plastic',
    },
  ];

  const containers = [
    {
      name: 'Stiklo Konteineris',
      color: 'hsl(150 50% 45%)',
      icon: Droplets,
      items: ['Stikliniai buteliai', 'Stiklainiai', 'Maisto stiklinė tara'],
    },
    {
      name: 'Popieriaus Konteineris',
      color: 'hsl(210 60% 50%)',
      icon: Package,
      items: ['Kartoninės dėžės', 'Popieriniai maišeliai', 'Laikraščiai'],
    },
    {
      name: 'Plastiko Konteineris',
      color: 'hsl(45 85% 55%)',
      icon: Trash2,
      items: ['Plastiko buteliai', 'Maisto pakuotės', 'Metalinės skardinės'],
    },
  ];

  const benefits = [
    {
      icon: TreeDeciduous,
      title: 'Saugome gamtą',
      description: 'Mažiname sąvartynų dydį ir taršą',
    },
    {
      icon: Recycle,
      title: 'Perdirbame išteklius',
      description: 'Atliekos tampa naujomis medžiagomis',
    },
    {
      icon: Leaf,
      title: 'Mažiname CO2',
      description: 'Kovojame prieš klimato kaitą',
    },
  ];

  const stats = [
    { value: '2.5M', label: 'Tonų perdirbta per metus' },
    { value: '89%', label: 'gyventojų rūšiuoja atliekas' },
    { value: '#4', label: 'vieta ES pagal rūšiavimą' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-subtle"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-64 h-64 bg-primary rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary rounded-full filter blur-3xl animate-float" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge className="bg-primary/10 text-primary hover:bg-primary/20 px-4 py-2 text-sm">
              Tvari Lietuva prasideda nuo mūsų
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight animate-fadeIn">
              Mokykimės Rūšiuoti
              <span className="block text-primary mt-2">Atliekas Kartu</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed animate-fadeIn" style={{ animationDelay: '0.1s' }}>
              Išsamus gidas apie atliekų rūšiavimą Lietuvoje. Raskite atsakymus, 
              kaip teisingai rūšiuoti, ir prisidėkite prie švarios aplinkos.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fadeIn" style={{ animationDelay: '0.2s' }}>
              <Link to="/rusiavimo-abc">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg">
                  Pradėti Mokytis
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/kaip-rusiuoti">
                <Button size="lg" variant="outline" className="px-8 py-6 text-lg">
                  Rūšiavimo Gairės
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <Card key={index} className="text-center hover-lift bg-card/50 backdrop-blur-sm border-2">
                <CardContent className="pt-6">
                  <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ką Rasite Šiame Gide?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Visa informacija, kurios reikia teisingam atliekų rūšiavimui vienoje vietoje
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Link key={index} to={feature.link}>
                  <Card className="h-full hover-lift cursor-pointer transition-smooth group">
                    <CardHeader>
                      <div className={`w-16 h-16 rounded-xl ${feature.color} bg-opacity-10 flex items-center justify-center mb-4 group-hover:scale-110 transition-smooth`}>
                        <Icon className="w-8 h-8" style={{ color: feature.color }} />
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                      <CardDescription className="text-base">
                        {feature.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button variant="ghost" className="w-full group-hover:bg-muted">
                        Sužinoti daugiau
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-smooth" />
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Containers Section */}
      <section className="py-16 md:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Rūšiavimo Konteineriai
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Kiekvienas konteineris skirtas tam tikroms atliekų rūšims
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {containers.map((container, index) => {
              const Icon = container.icon;
              return (
                <Card key={index} className="hover-lift">
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: container.color }}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <CardTitle className="text-xl">{container.name}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {container.items.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Kodėl Svarbu Rūšiuoti?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Kiekvienas surūšiuotas daiktas prisideda prie švarios aplinkos
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center space-y-4">
                  <div className="w-20 h-20 bg-gradient-hero rounded-2xl flex items-center justify-center mx-auto hover-lift">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-hero text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <AlertCircle className="w-16 h-16 mx-auto opacity-90" />
            <h2 className="text-3xl sm:text-4xl font-bold">
              Turite Klausimų?
            </h2>
            <p className="text-lg opacity-90">
              Neradote atsakymo į savo klausimą? Peržiūrėkite mūsų DUK skyrių 
              arba susisiekite su vietos atliekų tvarkymo įmone.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/mitai-ir-duk">
                <Button size="lg" variant="secondary" className="px-8">
                  DUK Skyrius
                </Button>
              </Link>
              <Link to="/rusiavimo-abc">
                <Button size="lg" variant="outline" className="px-8 bg-white/10 hover:bg-white/20 text-white border-white/30">
                  Rūšiavimo ABC
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;